import React, {useCallback, useState, useEffect, useRef} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useDispatch, useSelector} from "react-redux";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import CardActions from "@material-ui/core/CardActions";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import TextField from "@material-ui/core/TextField";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import {connect, setAdminAuthenticated, setFirmwareVersion, transceive, autoConnect} from "./actions";
import {byteToHexString, hexStringToString} from "./util";
import {useSnackbar} from "notistack";
import {Switch, LinearProgress, Box, Grid, FormControlLabel} from "@material-ui/core";
import {validatePin} from "./passwordValidator";
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import AdminPINRecovery from './AdminPINRecovery';
import FirmwareUpgrade from './FirmwareUpgrade';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  grid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(4),
    },
  },
  card: {
    margin: theme.spacing(2, 2, 3, 2), // 移动端: 16px 左右, 24px 底部
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(3, 3, 4, 3), // 平板: 24px 左右, 32px 底部
    },
    [theme.breakpoints.up('md')]: {
      marginLeft: "10%",
      marginRight: "10%",
      marginTop: "30px",
    },
  },
  buttonGroup: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginTop: theme.spacing(1),
    },
  }
}));

/* eslint-disable no-throw-literal */
export default function Overview() {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const device = useSelector(state => state.device);
  const model = useSelector(state => state.model);
  const firmwareVersion = useSelector(state => state.firmwareVersion);
  const dispatch = useDispatch();
  const authenticated = useSelector(state => state.adminAuthenticated);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [chPinDialogOpen, setChPinDialogOpen] = useState(false);
  const [changeCacheTimeDialogOpen, setChangeCacheTimeDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [cacheTime, setCacheTime] = useState(0);
  const [flashSpace, setFlashSpace] = useState('unknown');
  const [state, setState] = useState({
    led: false,
    ndefReadonly: false,
    sigTouch: false,
    decTouch: false,
    autTouch: false,
    cacheTime: 0,
    ndefEnabled: false,
    webusbLandingEnabled: false
  });
  const [firmwareFile, setFirmwareFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();

  // Optimization 1: ref for AdminPINRecovery
  const adminPINRecoveryRef = useRef(null);

  const onAuthenticate = useCallback(() => {
    setPinDialogOpen(true);
  }, []);

  const onChangePin = useCallback(() => {
    setChPinDialogOpen(true);
  }, []);

  const onChangeCacheTime = useCallback(() => {
    setChangeCacheTimeDialogOpen(true);
  }, []);

  const selectAdminApplet = useCallback(async () => {
    if (device === null) {
      // Try auto-connect first (no dialog)
      const autoConnected = await dispatch(autoConnect());
      if (!autoConnected) {
        // If auto-connect fails, show device picker
        if (!await dispatch(connect())) {
          throw 'Cannot connect to GriffonOakey';
        }
      }
    }
    let res = await dispatch(transceive("00A4040005F000000000"));
    if (!res.endsWith("9000")) {
      throw 'Selecting admin applet failed';
    }
    if (firmwareVersion === '') {
      res = await dispatch(transceive("0031000000"));
      if (res.endsWith("9000")) {
        let version = hexStringToString(res.substring(0, res.length - 4));
        dispatch(setFirmwareVersion(version));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, dispatch]); // 移除 firmwareVersion 依赖，避免状态变化导致的重复执行

  const adminTransceive = useCallback(async (capdu, success_msg, failed_msg, secret) => {
    try {
      let res = await dispatch(transceive(capdu, secret));
      if (res.endsWith("9000")) {
        enqueueSnackbar(success_msg, {variant: 'success'});
        return true;
      } else {
        enqueueSnackbar(failed_msg, {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
    return false;
  }, [dispatch, enqueueSnackbar]);

  // 加载设备信息（存储空间和配置）
  const loadDeviceInfo = useCallback(async () => {
    try {
      await selectAdminApplet();
      
      // read flash usage
      let res = await dispatch(transceive("0041000000"));
      if (res.endsWith("9000")) {
        let used = parseInt(res.substring(0, 2), 16);
        let total = parseInt(res.substring(2, 4), 16);
        setFlashSpace(`${used} KiB / ${total} KiB`);
      }
      
      // read configs
      res = await dispatch(transceive("0042000000"));
      if (res.endsWith("9000")) {
        if (firmwareVersion.toString() < "1.5") {
          let ledOn = res.substring(0, 2) === "01";
          let ndefReadonly = res.substring(4, 6) === "01";
          let sigTouch = res.substring(6, 8) === "01";
          let decTouch = res.substring(8, 10) === "01";
          let autTouch = res.substring(10, 12) === "01";
          let cacheTime = parseInt(res.substring(12, 14), 16);
          setState({
            led: ledOn,
            ndefReadonly: ndefReadonly,
            sigTouch: sigTouch,
            decTouch: decTouch,
            autTouch: autTouch,
            cacheTime: cacheTime
          });
        } else {
          let ledOn = res.substring(0, 2) === "01";
          let ndefReadonly = res.substring(4, 6) === "01";
          let ndefEnabled = res.substring(6, 8) === "01";
          let webusbLandingEnabled = res.substring(8, 10) === "01";
          setState({
            led: ledOn,
            ndefReadonly: ndefReadonly,
            sigTouch: false,
            decTouch: false,
            autTouch: false,
            cacheTime: 0,
            ndefEnabled: ndefEnabled,
            webusbLandingEnabled: webusbLandingEnabled
          });
        }
      }
    } catch (err) {
      console.error('Failed to load device info:', err);
    }
  }, [dispatch, selectAdminApplet, firmwareVersion]);

  const doAuthenticate = useCallback(async () => {
    setPinDialogOpen(false);
    try {
      let array = new TextEncoder().encode(pin);
      let len = new Uint8Array([array.length]);
      await selectAdminApplet();
      let res = await dispatch(transceive(`00200000${byteToHexString(len)}${byteToHexString(array)}`, true));
      if (res.endsWith("6983")) {
        // Optimization 1: Add "Reset Now" action button
        enqueueSnackbar('❌ Admin PIN is blocked!', {
          variant: 'error',
          autoHideDuration: 15000,
          action: (key) => (
            <>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  closeSnackbar(key);
                  // 直接打开 Factory Reset 对话框
                  if (adminPINRecoveryRef.current) {
                    adminPINRecoveryRef.current.openResetDialog();
                  }
                  // 同时滚动到卡片位置
                  setTimeout(() => {
                    const recoveryCard = document.querySelector('[data-recovery-card]');
                    if (recoveryCard) {
                      recoveryCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 300);
                }}
              >
                Reset Now
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => closeSnackbar(key)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          )
        });

        // Auto-scroll to Admin PIN Recovery card
        setTimeout(() => {
          const recoveryCard = document.querySelector('[data-recovery-card]');
          if (recoveryCard) {
            recoveryCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 500);
      } else if (res.startsWith("63C")) {
        let retry = parseInt(res.substr(3, 1), 16);
        enqueueSnackbar(`PIN verification failed, ${retry} retries left`, {variant: 'error'});
      } else if (res.endsWith("9000")) {
        dispatch(setAdminAuthenticated(true));
        enqueueSnackbar('PIN verification success', {variant: 'success'});
        // 加载设备信息
        await loadDeviceInfo();
      } else {
        enqueueSnackbar(`PIN verification failed (Status: ${res.slice(-4)})`, {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  }, [pin, dispatch, enqueueSnackbar, selectAdminApplet, loadDeviceInfo]);

  const doChangePin = useCallback(async () => {
    // 🔒 安全增强：验证两次输入是否一致
    if (newPin !== confirmPin) {
      enqueueSnackbar('New PIN and confirmation do not match', {variant: 'warning'});
      return;
    }
    
    // 🔒 安全增强：使用增强的密码验证
    const validation = validatePin(newPin, {minLength: 6, maxLength: 64, allowWeak: false});
    if (!validation.valid) {
      enqueueSnackbar(validation.error, {variant: 'error'});
      return;
    }
    if (validation.warning) {
      enqueueSnackbar(validation.warning, {variant: 'warning'});
      // 继续执行，只是警告
    }
    
    setChPinDialogOpen(false);
    try {
      let array = new TextEncoder().encode(newPin);
      let len = new Uint8Array([array.length]);
      let res = await dispatch(transceive(`00210000${byteToHexString(len)}${byteToHexString(array)}`, true));
      if (res.endsWith("9000")) {
        dispatch(setAdminAuthenticated(true));
        enqueueSnackbar('PIN changed successfully', {variant: 'success'});
        // 清空输入框
        setNewPin('');
        setConfirmPin('');
      } else {
        enqueueSnackbar('Change PIN failed', {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  }, [newPin, confirmPin, dispatch, enqueueSnackbar]);

  const doChangeCacheTime = useCallback(async () => {
    setChangeCacheTimeDialogOpen(false);
    await adminTransceive(`000903${byteToHexString([cacheTime])}`, "Cache time changed", "Change cache time failed");
    setState(oldState => ({...oldState, cacheTime: cacheTime}));
  }, [cacheTime, adminTransceive]);

  // 页面加载时，如果已认证，自动加载设备信息
  useEffect(() => {
    if (device && authenticated) {
      console.log('✓ Admin authenticated, loading device info...');
      loadDeviceInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, authenticated]); // 只在 device 或 authenticated 变化时触发

  const onKeyPress = useCallback(async (e) => {
    if (e.key === 'Enter') {
      await doAuthenticate();
    }
  }, [doAuthenticate]);


  const onKeyPressChangeCacheTime = useCallback(async (e) => {
    if (e.key === 'Enter') {
      await doChangeCacheTime();
    }
  }, [doChangeCacheTime]);

  const setLedStatus = useCallback(async (e) => {
    let ledOn = e.target.checked;
    if (ledOn) {
      await adminTransceive("00400101", "LED is on", "Set LED status failed");
    } else {
      await adminTransceive("00400100", "LED is off", "Set LED status failed");
    }
    setState(oldState => ({...oldState, led: ledOn}));
  }, [adminTransceive]);

  const setNDEFReadonly = useCallback(async (e) => {
    let readonly = e.target.checked;
    if (readonly) {
      await adminTransceive("00080100", "NDEF is RO", "Set NDEF status failed");
    } else {
      await adminTransceive("00080000", "NDEF is RW", "Set NDEF status failed");
    }
    setState(oldState => ({...oldState, ndefReadonly: readonly}));
  }, [adminTransceive]);

  const setNDEFStatus = useCallback(async (e) => {
    let enabled = e.target.checked;
    if (enabled) {
      await adminTransceive("00400401", "NDEF is enabled", "Set NDEF status failed");
    } else {
      await adminTransceive("00400400", "NDEF is disabled", "Set NDEF status failed");
    }
    setState(oldState => ({...oldState, ndefEnabled: enabled}));
  }, [adminTransceive]);

  const setWebUSBLandingStatus = useCallback(async (e) => {
    let enabled = e.target.checked;
    if (enabled) {
      await adminTransceive("00400501", "WebUSB prompt is enabled", "Set WebUSB prompt failed");
    } else {
      await adminTransceive("00400500", "WebUSB prompt disabled", "Set WebUSB prompt failed");
    }
    setState(oldState => ({...oldState, webusbLandingEnabled: enabled}));
  }, [adminTransceive]);

  const resetOpenPGP = useCallback(async () => {
    await adminTransceive("00030000", "Reset OpenPGP done", "Reset OpenPGP failed");
  }, [adminTransceive]);

  const resetPIV = useCallback(async () => {
    await adminTransceive("00040000", "Reset PIV done", "Reset PIV failed");
  }, [adminTransceive]);

  const resetOATH = useCallback(async () => {
    await adminTransceive("00050000", "Reset OATH done", "Reset OATH failed");
  }, [adminTransceive]);

  const clearOATHPassword = useCallback(async () => {
    try {
      // 选择 OATH applet
      let res = await dispatch(transceive("00A4040008A000000527210101"));
      if (!res.endsWith("9000")) {
        enqueueSnackbar("Failed to select OATH applet", { variant: "error" });
        return;
      }
      
      // 发送 SET_CODE 指令，LC=0 表示清除密码
      res = await dispatch(transceive("0003000000"));
      if (res.endsWith("9000")) {
        enqueueSnackbar("OATH password cleared successfully", { variant: "success" });
      } else {
        enqueueSnackbar("Failed to clear OATH password: " + res, { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Error clearing OATH password: " + err, { variant: "error" });
    }
  }, [dispatch, enqueueSnackbar]);

  const resetNDEF = useCallback(async () => {
    await adminTransceive("00070000", "Reset NDEF done", "Reset NDEF failed");
  }, [adminTransceive]);

  const enterDFU = useCallback(async () => {
    await adminTransceive("00FF2222", "Enter DFU: Unexpected success",
      "Enter DFU: WebUSB disconnected, device should be in DFU now");
  }, [adminTransceive]);

  const setSigTouch = useCallback(async (e) => {
    let touch = e.target.checked;
    if (touch) {
      await adminTransceive("00090001", "Set SIG touch policy REQUIRED", "Set SIG touch policy failed");
    } else {
      await adminTransceive("00090000", "Set SIG touch policy NO", "Set SIG touch policy failed");
    }
    setState(oldState => ({...oldState, sigTouch: touch}));
  }, [adminTransceive]);

  const setDecTouch = useCallback(async (e) => {
    let touch = e.target.checked;
    if (touch) {
      await adminTransceive("00090101", "Set DEC touch policy REQUIRED", "Set DEC touch policy failed");
    } else {
      await adminTransceive("00090100", "Set DEC touch policy NO", "Set DEC touch policy failed");
    }
    setState(oldState => ({...oldState, decTouch: touch}));
  }, [adminTransceive]);

  const setAutTouch = useCallback(async (e) => {
    let touch = e.target.checked;
    if (touch) {
      await adminTransceive("00090201", "Set AUT touch policy REQUIRED", "Set AUT touch policy failed");
    } else {
      await adminTransceive("00090200", "Set AUT touch policy NO", "Set AUT touch policy failed");
    }
    setState(oldState => ({...oldState, autTouch: touch}));
  }, [adminTransceive]);

  const handleFirmwareFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 256 * 1024) {
        enqueueSnackbar('Firmware file too large (max 256KB)', {variant: 'error'});
        return;
      }
      setFirmwareFile(file);
      enqueueSnackbar(`Firmware file selected: ${file.name} (${file.size} bytes)`, {variant: 'info'});
    }
  }, [enqueueSnackbar]);

  const uploadFirmware = useCallback(async () => {
    if (!firmwareFile) {
      enqueueSnackbar('Please select a firmware file first', {variant: 'error'});
      return;
    }

    if (!window.confirm('Are you sure you want to update firmware? This operation cannot be undone and may brick your device if interrupted.')) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Read firmware file
      const reader = new FileReader();
      const firmwareData = await new Promise((resolve, reject) => {
        reader.onload = (e) => resolve(new Uint8Array(e.target.result));
        reader.onerror = reject;
        reader.readAsArrayBuffer(firmwareFile);
      });

      enqueueSnackbar('Starting firmware upload...', {variant: 'info'});

      // Step 1: Start firmware upload
      const sizeBytes = [
        (firmwareData.length >> 24) & 0xFF,
        (firmwareData.length >> 16) & 0xFF,
        (firmwareData.length >> 8) & 0xFF,
        firmwareData.length & 0xFF
      ];
      let res = await dispatch(transceive(`006000000${4}${byteToHexString(sizeBytes)}`));
      if (!res.endsWith("9000")) {
        throw 'Failed to start firmware upload';
      }

      // Step 2: Upload firmware data in chunks (max 255 bytes per APDU)
      const chunkSize = 255;
      const totalChunks = Math.ceil(firmwareData.length / chunkSize);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, firmwareData.length);
        const chunk = firmwareData.slice(start, end);
        
        res = await dispatch(transceive(`006100000${chunk.length}${byteToHexString(chunk)}`));
        if (!res.endsWith("9000")) {
          throw `Failed to upload firmware chunk ${i + 1}/${totalChunks}`;
        }
        
        setUploadProgress(Math.floor((i + 1) * 100 / totalChunks));
      }

      enqueueSnackbar('Firmware uploaded successfully, verifying...', {variant: 'success'});

      // Step 3: Verify firmware
      res = await dispatch(transceive("0062000000"));
      if (!res.endsWith("9000")) {
        throw 'Firmware verification failed';
      }

      enqueueSnackbar('Firmware verified, committing...', {variant: 'success'});

      // Step 4: Commit firmware (device will reboot)
      res = await dispatch(transceive("0063000000"));
      if (!res.endsWith("9000")) {
        throw 'Firmware commit failed';
      }

      enqueueSnackbar('Firmware update completed! Device will reboot now.', {variant: 'success'});
      setUploadProgress(100);
      
      // Clear file selection
      setFirmwareFile(null);
      
    } catch (error) {
      enqueueSnackbar(`Firmware update failed: ${error}`, {variant: 'error'});
      
      // Try to abort the update
      try {
        await dispatch(transceive("0064000000"));
      } catch (e) {
        // Ignore abort errors
      }
    } finally {
      setIsUploading(false);
    }
  }, [firmwareFile, dispatch, enqueueSnackbar]);

  return (
    <div className={classes.root}>
      <Grid container spacing={1} justifyContent={'center'} className={classes.grid}>
        <Grid item xs={10}>
          <Card>
            <CardContent>
              <Typography variant="h3">
                Admin Applet
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                System administration, device configuration, and firmware management.
              </Typography>
              <Box mt={2}>
                <Typography variant="body2">
                  <strong>Authentication Status:</strong> {authenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
                </Typography>
                <Typography variant="body2">
                  <strong>Storage Usage:</strong> {flashSpace}
                </Typography>
              </Box>
            </CardContent>
            <CardActions>
              <Button onClick={onAuthenticate} variant="contained" color="primary">
                Authenticate
              </Button>
            </CardActions>
          </Card>
        </Grid>
      {
        authenticated ? (
          <>
            <Grid item xs={10}>
              <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Config
                </Typography>
                <Box mt={2}>
                  <FormControlLabel
                    control={<Switch checked={state.led || false} onChange={setLedStatus} color="primary" />}
                    label="LED"
                  />
                </Box>
                <Box mt={1}>
                  <FormControlLabel
                    control={<Switch checked={state.ndefReadonly || false} onChange={setNDEFReadonly} color="primary" />}
                    label="NDEF readonly"
                  />
                </Box>
                {
                  firmwareVersion.toString() < "1.5" ? (
                    <Box mt={2}>
                      <FormControlLabel
                        control={<Switch checked={state.sigTouch || false} onChange={setSigTouch} color="primary" />}
                        label="OpenPGP SIG touch policy"
                      />
                      <FormControlLabel
                        control={<Switch checked={state.decTouch || false} onChange={setDecTouch} color="primary" />}
                        label="OpenPGP DEC touch policy"
                      />
                      <FormControlLabel
                        control={<Switch checked={state.autTouch || false} onChange={setAutTouch} color="primary" />}
                        label="OpenPGP AUT touch policy"
                      />
                      <Box mt={2}>
                        <Typography variant="body2" color="textSecondary">
                          OpenPGP touch cache time: <strong>{state.cacheTime} seconds</strong>
                        </Typography>
                        <Button variant="outlined" color="primary" onClick={onChangeCacheTime} style={{marginTop: 8}}>
                          Change Cache Time
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box mt={2}>
                      <FormControlLabel
                        control={<Switch checked={state.ndefEnabled || false} onChange={setNDEFStatus} color="primary" />}
                        label="NDEF enabled"
                      />
                      <FormControlLabel
                        control={<Switch checked={state.webusbLandingEnabled || false} onChange={setWebUSBLandingStatus} color="primary" />}
                        label="WebUSB prompt enabled"
                      />
                    </Box>
                  )
                }
                
                <Box mt={2}>
                  <Button variant="outlined" color="primary" onClick={onChangePin} style={{marginRight: 8}}>
                    Change PIN
                  </Button>
                  {model.toString().indexOf("STM32") >= 0 && (
                    <>
                      <Button variant="outlined" color="secondary" onClick={enterDFU} style={{marginRight: 8}}>
                        Enter DFU (development only)
                      </Button>
                      <Button variant="outlined" color="primary" onClick={() => window.location = 'https://dfu.canokeys.org/'}>
                        Go to Web DFU util
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
            </Grid>
            <Grid item xs={10}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Reset Applet
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  ⚠️ Warning: These operations will erase all data in the selected applet.
                </Typography>
                
                <Box mt={2}>
                  <Button onClick={resetOpenPGP} variant="outlined" color="secondary" style={{marginRight: 8}}>
                    Reset OpenPGP
                  </Button>
                  <Button onClick={resetPIV} variant="outlined" color="secondary" style={{marginRight: 8}}>
                    Reset PIV
                  </Button>
                  <Button onClick={resetOATH} variant="outlined" color="secondary" style={{marginRight: 8}}>
                    Reset OATH
                  </Button>
                  <Button onClick={resetNDEF} variant="outlined" color="secondary">
                    Reset NDEF
                  </Button>
                </Box>
              </CardContent>
            </Card>
            </Grid>
            <Grid item xs={10}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  OATH Security Settings
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Manage OATH password protection. With unified authentication enabled, 
                  you can use Admin PIN to access OATH without setting a separate password.
                </Typography>
              </CardContent>
              <CardActions>
                <Button onClick={clearOATHPassword} variant="contained" color="primary">
                  Clear OATH Password
                </Button>
              </CardActions>
            </Card>
            </Grid>
            <Grid item xs={10}>
              <FirmwareUpgrade />
            </Grid>
            <Grid item xs={10}>
              <AdminPINRecovery ref={adminPINRecoveryRef} />
            </Grid>
          </>
        ) : null
      }
      </Grid>
      <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)} fullScreen={fullScreen}>
        <DialogTitle>Enter PIN to Authenticate Admin Applet</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter PIN below. The default is 123456. Please be aware of PIN retry count. This will not be stored in
            browser.
          </DialogContentText>
          <TextField
            type="password"
            autoFocus
            fullWidth
            inputProps={{maxLength: 64}}
            onKeyPress={onKeyPress}
            onChange={(e) => setPin(e.target.value)}/>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={doAuthenticate}>
            Authenticate
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={chPinDialogOpen} onClose={() => setChPinDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={fullScreen}>
        <form onSubmit={(e) => { e.preventDefault(); doChangePin(); }}>
          <DialogTitle>Change Admin PIN</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter a new PIN for Admin Applet (at least 6 characters).
            </DialogContentText>
            <TextField
              type="password"
              label="New PIN"
              autoFocus
              fullWidth
              margin="dense"
              value={newPin}
              inputProps={{maxLength: 64}}
              onChange={(e) => setNewPin(e.target.value)}
              autoComplete="new-password"
            />
            <TextField
              type="password"
              label="Confirm New PIN"
              fullWidth
              margin="dense"
              value={confirmPin}
              inputProps={{maxLength: 64}}
              onChange={(e) => setConfirmPin(e.target.value)}
              autoComplete="new-password"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChPinDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Change PIN
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={changeCacheTimeDialogOpen} onClose={() => setChangeCacheTimeDialogOpen(false)} fullScreen={fullScreen}>
        <DialogTitle>Enter new cache time</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter new cache time for OpenPGP touch (in seconds): 0 ~ 255.
          </DialogContentText>
          <TextField
            type="number"
            autoFocus
            fullWidth
            InputProps={{ inputProps: { min: 0, max: 255 } }}
            onKeyPress={onKeyPressChangeCacheTime}
            onChange={(e) => setCacheTime(parseInt(e.target.value))}/>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={doChangeCacheTime}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
